import { ChevronRight } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';

import type { AspectRollup } from './gap-analysis.utils';
import { competitionLabel, formatCount } from './gap-analysis.utils';

interface Props {
  /** Filtered rollups — the table shows exactly what the comparison above shows. */
  rollups: AspectRollup[];
  /** Keyed to the *unfiltered* product, so the keyword columns don't appear and
   *  vanish as filters change. */
  showKeywordColumns: boolean;
  /** Attribute count before filtering, so the label can say "N of M". */
  totalRows: number;
  isFiltered: boolean;
  onClearFilters: () => void;
}

/** Every row flattened out of its rollup, carrying the category and the status
 *  label the rest of the tab uses. */
function flattenRows(rollups: AspectRollup[]) {
  return rollups.flatMap((rollup) =>
    [
      ...rollup.added.map((row) => ({ row, status: 'New' })),
      ...rollup.restated.map((row) => ({ row, status: 'Restated' })),
      ...rollup.had.map((row) => ({ row, status: 'Already there' })),
    ].map(({ row, status }) => ({
      aspect: rollup.aspect,
      tag: row.tag,
      status,
      searchVolume: row.searchVolume ?? 0,
      competition: competitionLabel(row.competitionLevel),
      matchedTerm: row.matchedTerm,
    })),
  );
}

/**
 * The exhaustive per-attribute view, collapsed by default. It exists for the
 * cases the panels above deliberately summarise away — matched terms, exact
 * volumes — without spending the screen on them by default.
 */
export function GapAttributeTable({
  rollups,
  showKeywordColumns,
  totalRows,
  isFiltered,
  onClearFilters,
}: Props) {
  const rows = flattenRows(rollups);

  return (
    <Collapsible className="border-t" data-testid="gap-analysis-attribute-table-section">
      <CollapsibleTrigger
        data-testid="gap-analysis-attribute-table-toggle"
        className="hover:bg-muted/50 text-muted-foreground hover:text-foreground group flex w-full cursor-pointer items-center gap-2 px-5 py-3 text-left text-[12.5px] font-medium"
      >
        <ChevronRight
          className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden="true"
        />
        {/* The table follows the filters above it, so the label has to say so —
            "All attributes" while filtered would contradict what it renders. */}
        {isFiltered
          ? `Filtered attributes (${String(rows.length)} of ${String(totalRows)})`
          : `All attributes (${String(totalRows)})`}
        {isFiltered ? (
          <span
            className="border-primary/30 bg-primary/10 text-primary rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
            data-testid="gap-analysis-attribute-table-filtered-badge"
          >
            Filtered
          </span>
        ) : null}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-5 pb-5">
          <div className="overflow-x-auto rounded-lg border">
            <Table data-testid="gap-analysis-attribute-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Attribute</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  {showKeywordColumns ? (
                    <>
                      <TableHead className="text-right">Search volume</TableHead>
                      <TableHead>Competition</TableHead>
                      <TableHead>Matched term</TableHead>
                    </>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  // `tag` isn't unique within a product — the same value can
                  // appear on both sides — so the index tie-breaks the key.
                  <TableRow
                    key={`${row.aspect}-${row.tag}-${String(index)}`}
                    data-testid={`gap-analysis-attribute-table-row-${row.aspect}-${row.status.toLowerCase().replace(/\s+/g, '-')}-${row.tag}`}
                  >
                    <TableCell className="text-foreground font-medium">{row.tag}</TableCell>
                    <TableCell className="text-muted-foreground capitalize">{row.aspect}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    {showKeywordColumns ? (
                      <>
                        <TableCell className="text-right tabular-nums">
                          {row.searchVolume > 0 ? (
                            formatCount(row.searchVolume)
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.competition ?? <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.matchedTerm ?? '—'}
                        </TableCell>
                      </>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {rows.length === 0 ? (
              <div
                className="flex flex-col items-center gap-2 py-12 text-center"
                data-testid="gap-analysis-attribute-table-empty"
              >
                <p className="text-foreground text-sm font-semibold">
                  No attributes match these filters.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClearFilters}
                  className="cursor-pointer"
                  data-testid="gap-analysis-attribute-table-clear-filters-button"
                >
                  Clear filters
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
